import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';

/**
 * <free-delivery-announcement>
 *
 * A free-shipping progress message for the announcement bar. Because the
 * announcement bar lives in the header (which is NOT re-rendered on cart
 * changes, unlike the cart drawer), this element listens for cart updates and
 * recomputes the remaining amount client-side against the Cart → Free delivery
 * threshold (passed in via data attributes).
 *
 * The initial state is rendered server-side in Liquid, so this only handles
 * subsequent cart changes.
 *
 * Data attributes:
 *   data-threshold        Threshold in minor units (cents).
 *   data-progress-template Message shown while below threshold; `[amount]` is
 *                          replaced with the remaining amount.
 *   data-success-text     Message shown once the threshold is reached.
 *   data-money-format     Shopify money format template (e.g. "£{{amount}}").
 *   data-currency         ISO currency code (e.g. "GBP").
 */
class FreeDeliveryAnnouncement extends HTMLElement {
  /** @type {HTMLElement | null} */
  #textEl = null;
  /** @type {AbortController | null} */
  #inFlight = null;

  connectedCallback() {
    this.#textEl = this.querySelector('[data-free-delivery-text]');
    this.threshold = parseInt(this.dataset.threshold ?? '0', 10);
    this.progressTemplate = this.dataset.progressTemplate ?? 'Spend [amount] more for free shipping';
    this.successText = this.dataset.successText ?? 'Free shipping unlocked';
    this.moneyFormat = this.dataset.moneyFormat ?? '{{amount}}';
    this.currency = this.dataset.currency || 'USD';

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#inFlight?.abort();
  }

  #onCartUpdate = async (event) => {
    // Prefer the total carried on the event; fall back to the authoritative
    // /cart.js when the source doesn't include it (e.g. product-form adds).
    let totalCents = event?.detail?.resource?.total_price;

    if (typeof totalCents !== 'number') {
      totalCents = await this.#fetchCartTotal();
    }

    if (typeof totalCents === 'number') this.#render(totalCents);
  };

  /** @returns {Promise<number | null>} */
  async #fetchCartTotal() {
    this.#inFlight?.abort();
    this.#inFlight = new AbortController();

    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        signal: this.#inFlight.signal,
      });
      if (!response.ok) return null;
      const cart = await response.json();
      return typeof cart.total_price === 'number' ? cart.total_price : null;
    } catch (_) {
      return null;
    }
  }

  /** @param {number} totalCents */
  #render(totalCents) {
    if (!this.#textEl) return;

    const remaining = this.threshold - totalCents;

    if (remaining <= 0) {
      this.setAttribute('data-complete', '');
      this.#textEl.textContent = this.successText;
    } else {
      this.removeAttribute('data-complete');
      const amount = formatMoney(remaining, this.moneyFormat, this.currency);
      this.#textEl.textContent = this.progressTemplate.replaceAll('[amount]', amount);
    }
  }
}

if (!customElements.get('free-delivery-announcement')) {
  customElements.define('free-delivery-announcement', FreeDeliveryAnnouncement);
}
